

import { Guest } from "@/types/Guest";
import { CreationFieldsRef } from "../../PCreateGuest/PCreateGuest";
import { GuestDetailLayoutView } from "../layout/GuestDetailLayoutView";
import type { EntityLaneSurface } from "../layout/createLayoutSurface";
import { EmailUndeliverableNotice } from "./EmailUndeliverableNotice";

type GuestInfosProps = {
  eventId?: string;
  creationFields: CreationFieldsRef;
  setCreationFieldsAction: (fields: CreationFieldsRef) => void;
  notes: boolean;
  currentGuestId?: string;
  /** The fetched guest — value source for the layout's DISPLAY-ONLY cells
   *  (Import, Source, Created at, check-in, companion count). Pass-through
   *  only; editable fields keep coming from `creationFields`. */
  guest?: Guest;
  onSaveCredential?: (fieldName: string, value: string) => Promise<void>;
  additionalFieldErrors?: Record<string, string>;
  onClearFieldError?: (fieldName: string) => void;
  /** IMAGE upload confirm persisted a value — host syncs form + baseline. */
  onAdditionalFieldPersisted?: (fieldName: string, value: unknown) => void;
  /** Pass-through to the layout: a cell's box disagrees with the form value. */
  onFieldInvalid?: (fieldName: string, invalid: boolean) => void;
  /** Render a request lane's layout instead of the guest-detail one — set for
   *  a PENDING request, see GuestDetailBody.requestSurface. Pass-through; the
   *  guest exists, hence `existingGuest` below it. */
  surface?: EntityLaneSurface;
  /** Rendered under the layout, inside this scrolling pane — the arrival-
   *  notice block of the full popup (W4 Hop B); the nested companion view
   *  and the pending-request view pass nothing. */
  afterLayout?: React.ReactNode;
  /** Pass-through: the popup shows an archived guest (see GuestDetailLayoutView). */
  archived?: boolean;
};

/**
 * The info panel of the guest detail popup. The fields themselves are rendered
 * by the event's (customizable) guest-detail layout — see
 * GuestDetailLayoutView; this wrapper only provides the scrolling panel chrome
 * that differs between the main popup (notes=true, bordered card next to the
 * notes column) and the inline nested companion view (notes=false).
 */
export const GuestInfos: React.FC<GuestInfosProps> = ({
  eventId,
  creationFields,
  setCreationFieldsAction,
  notes,
  currentGuestId,
  guest,
  onSaveCredential,
  additionalFieldErrors,
  onClearFieldError,
  onAdditionalFieldPersisted,
  onFieldInvalid,
  surface,
  afterLayout,
  archived,
}) => {
  return (
    <div
      // `shrink-0` + `md:min-h-0`, NOT an unconditional `min-h-0` — the exact
      // recipe from ItemDetailLayout, and for its reason: below md the BODY is
      // the single scrollport, so a 0 floor here means all shrinkage lands on
      // the sibling. The old `max-h-[50%]` jailed the form in half the sheet;
      // removing it without also giving BOTH children a floor collapsed
      // GuestNotes to 2px (measured). From md up each pane owns a scrollport,
      // so the floor is released again.
      className={`w-full shrink-0 flex flex-col p-4 sm:p-6 surface-primary rounded-2xl md:min-h-0 md:flex-1 md:min-w-0 md:shrink md:overflow-y-auto ${
        notes ? "border outline-quaternary" : ""
      }`}
    >
      {/* Above the layout, and outside it: a bounced address silences every
          mail to this guest including their ticket, and no layout edit may be
          able to hide that. */}
      <EmailUndeliverableNotice guest={guest} eventId={eventId} />
      <GuestDetailLayoutView
        surface={surface}
        existingGuest
        eventId={eventId}
        creationFields={creationFields}
        setCreationFieldsAction={setCreationFieldsAction}
        currentGuestId={currentGuestId}
        guest={guest}
        onSaveCredential={onSaveCredential}
        additionalFieldErrors={additionalFieldErrors}
        onClearFieldError={onClearFieldError}
        onAdditionalFieldPersisted={onAdditionalFieldPersisted}
        onFieldInvalid={onFieldInvalid}
        archived={archived}
      />
      {afterLayout}
    </div>
  );
};
